// Traduz o conteúdo executado do relatório (títulos de fase, labels e respostas de
// select) para o idioma da interface, usando as variantes pt/en/es do modelo de teste.
//
// Pontos importantes do funcionamento:
// - Cada variante de idioma do modelo (test.pt / test.en / test.es) tem ids PRÓPRIOS
//   para steps e fields. O relatório executado guarda os ids da variante em que foi
//   executado (tipicamente pt). Por isso o pareamento é feito contra a variante BASE
//   (a que casa por id/título) e a tradução usa o step/field de MESMO ÍNDICE na
//   variante alvo — nunca por id direto na variante alvo.
// - Fases que não existem no modelo (ex.: "Detalhes do Teste"/"Rastreabilidade",
//   preenchidas retroativamente em relatórios antigos) caem num dicionário de
//   fallback pt→en.
const LANGS = ['pt', 'en', 'es'];

// Dicionário de fallback pt→en para conteúdo sem contraparte no modelo de teste.
const FALLBACK_EN = {
    // Títulos de seção
    'Pré Inspeção': 'Pre-Inspection',
    'Inspeção Visual': 'Visual Inspection',
    'Revisão': 'Review',
    'Ensaio Elétrico': 'Electrical Test',
    'Detalhes do Teste': 'Test Details',
    'Rastreabilidade': 'Traceability',
    'Validação Final': 'Final Validation',
    'Evidência': 'Evidence',
    // Labels de campos
    'Bitola conforme o projeto?': 'Cable size according to the project?',
    'Encaminhamento conforme o projeto?': 'Routing according to the project?',
    'Cabo sem emenda no trecho?': 'Cable without splice in the section?',
    'Encaminhamento livre de canto vivo?': 'Routing free of sharp edges?',
    'Terminais prensados de forma correta e isolados?': 'Terminals properly crimped and insulated?',
    'Duração do Teste (hh:mm)': 'Test Duration (hh:mm)',
    'Tensão Aplicada (V)': 'Applied Voltage (V)',
    'Comprimento do Cabo (m)': 'Cable Length (m)',
    'Temperatura ambiente (ºC)': 'Ambient Temperature (ºC)',
    'Umidade Relativa (%)': 'Relative Humidity (%)',
    'Instrumento 1': 'Instrument 1',
    'Instrumento 2': 'Instrument 2',
    'Instrumento 3': 'Instrument 3',
    'Certificado de Calibração 1': 'Calibration Certificate 1',
    'Certificado de Calibração 2': 'Calibration Certificate 2',
    'Certificado de Calibração 3': 'Calibration Certificate 3',
    'Validade do Certificado 1': 'Certificate Validity 1',
    'Validade do Certificado 2': 'Certificate Validity 2',
    'Validade do Certificado 3': 'Certificate Validity 3',
    'Conformidade com o Projeto': 'Conformity with the Project',
    'Identificação e Tag': 'Identification and Tag',
    'Continuidade e Faseamento': 'Continuity and Phasing',
    'Limpeza Geral': 'General Cleaning',
    'Classe do Parafuso': 'Screw Class',
    'Torque das Conexões': 'Connection Torque',
    'Lacre dos Parafusos': 'Screw Seal',
    'Distâncias de Isolamento': 'Insulation Distances',
    'Material de Isolação': 'Insulation Material',
    'Condutores de Fase': 'Phase Conductors',
    'Condutores de Neutro': 'Neutral Conductors',
    'Condutores de Terra': 'Ground Conductors',
    'Tensão do Cabo': 'Cable Voltage',
    'Condutor': 'Conductor',
    'Espaçamento entre Cabos': 'Cable Spacing',
    'Infraestrutura': 'Infrastructure',
    'Classe de Tensão (kV)': 'Voltage Class (kV)',
    'Condutores por Fase': 'Conductors per Phase',
    'Bitola de cabos de Fase': 'Phase Cable Gauge',
    'Bitola de Cabos de Neutro': 'Neutral Cable Gauge',
    'Bitola de Cabos de Terra': 'Ground Cable Gauge',
    'Sistema': 'System',
    'Isolação': 'Insulation',
    'Maneira de Instalar': 'Installation Method',
    // Respostas (valores de select / texto padronizado)
    'Sim': 'Yes',
    'Não': 'No',
    'EPR ou XLPE': 'EPR or XLPE',
    'POLIOLEFINA': 'POLYOLEFIN',
    'COBRE': 'COPPER',
    'ALUMINIO': 'ALUMINUM',
    'ALUMÍNIO': 'ALUMINUM',
    'LEITO': 'CABLE TRAY',
    'ELETROCALHA LISA C/ TAMPA': 'SOLID CABLE TRAY WITH COVER',
    'ELETRODUTO': 'CONDUIT',
    'CONTÍGUOS': 'CONTIGUOUS',
    'TRIFÓLIO (3F+T)': 'TREFOIL (3F+G)',
    'TRIFÓLIO (3F+N+T)': 'TREFOIL (3F+N+G)',
    '3F+T TRIFÓLIO': '3F+G TREFOIL',
    'EPR  90ºC ALUMÍNIO': 'EPR 90ºC ALUMINUM',
};

class ReportTranslationService {
    static fallbackTranslate(text, language) {
        if (language !== 'en' || typeof text !== 'string') return text;
        if (FALLBACK_EN[text]) return FALLBACK_EN[text];
        // "Fase R x S", "Fase Pos x Neg" etc. (títulos de medições do ensaio elétrico)
        if (/^Fase /.test(text)) return text.replace(/^Fase /, 'Phase ');
        return text;
    }

    static matchByIdOrTitle(list, candidate) {
        if (!Array.isArray(list)) return -1;
        const ids = [candidate?.id, candidate?._id, candidate?.code].filter(Boolean).map(String);
        const title = candidate?.title ?? candidate?.label;
        return list.findIndex((item) => {
            const itemIds = [item?.id, item?._id, item?.code].filter(Boolean).map(String);
            if (ids.length && itemIds.some((id) => ids.includes(id))) return true;
            const itemTitle = item?.title ?? item?.label;
            return title != null && itemTitle === title;
        });
    }

    // Encontra a variante do modelo cujo conteúdo casa com o que foi executado
    // (ids/títulos do relatório vêm da variante em que o teste foi executado).
    static findBaseMatch(test, candidate, stepsOf) {
        for (const lang of LANGS) {
            const steps = stepsOf(test?.[lang]);
            const index = this.matchByIdOrTitle(steps, candidate);
            if (index !== -1) return { steps, index };
        }
        return null;
    }

    static translateValue(value, baseField, targetField, language) {
        const baseOptions = baseField?.options || baseField?.choices;
        const targetOptions = targetField?.options || targetField?.choices;
        if (Array.isArray(baseOptions) && Array.isArray(targetOptions)) {
            const idx = baseOptions.indexOf(value);
            if (idx !== -1 && targetOptions[idx] != null) return targetOptions[idx];
        }
        return this.fallbackTranslate(value, language);
    }

    static translatePhase(phase, baseStep, targetStep, language) {
        const translated = {
            ...phase,
            title: targetStep?.title ?? this.fallbackTranslate(phase?.title, language),
        };

        if (phase?.type === 'form' && Array.isArray(phase.fields)) {
            translated.fields = phase.fields.map((field) => {
                const idx = this.matchByIdOrTitle(baseStep?.fields, field);
                const baseField = idx !== -1 ? baseStep.fields[idx] : null;
                const targetField = idx !== -1 ? targetStep?.fields?.[idx] : null;

                const mapValue = (v) => this.translateValue(v, baseField, targetField, language);
                let userGivenValue = field?.userGivenValue;
                if (userGivenValue != null) {
                    userGivenValue = Array.isArray(userGivenValue)
                        ? userGivenValue.map(mapValue)
                        : mapValue(userGivenValue);
                }

                const targetOptions = targetField?.options || targetField?.choices;
                return {
                    ...field,
                    label: targetField?.label ?? this.fallbackTranslate(field?.label, language),
                    choices: Array.isArray(targetOptions) ? targetOptions : field?.choices,
                    options: Array.isArray(targetOptions) ? targetOptions : field?.options,
                    userGivenValue,
                };
            });
        }

        if (phase?.type === 'camera' && Array.isArray(phase.steps)) {
            translated.steps = phase.steps.map((step) => {
                const idx = this.matchByIdOrTitle(baseStep?.steps, step);
                const targetTitle = idx !== -1 ? targetStep?.steps?.[idx]?.title : null;
                return {
                    ...step,
                    title: targetTitle ?? this.fallbackTranslate(step?.title, language),
                };
            });
        }

        return translated;
    }

    static translateReport(report, language) {
        if (!report || !Array.isArray(report.phase)) return report;
        if (language === 'pt') return report; // conteúdo executado já está em pt

        const test = report.test;
        const targetSteps = test?.[language]?.plan?.steps;
        const stepsOf = (variant) => variant?.plan?.steps;

        const translatedPhases = report.phase.map((phase) => {
            const baseMatch = test ? this.findBaseMatch(test, phase, stepsOf) : null;
            const baseStep = baseMatch ? baseMatch.steps[baseMatch.index] : null;
            const targetStep = baseMatch && Array.isArray(targetSteps) ? targetSteps[baseMatch.index] : null;
            return this.translatePhase(phase, baseStep, targetStep, language);
        });

        return {
            ...report,
            phase: translatedPhases,
        };
    }
}

export default ReportTranslationService;
